import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, Input, Dimmer, Loader, Segment, Checkbox, Grid } from 'semantic-ui-react';
import * as _ from 'lodash';
import ApiService from '../../../../service/apiService';
import { generateMultipleTask } from './redux/actions';
import Joyn2Button from '../../../common/Joyn2Button';
import CustomDropdown from '../../../common/CustomDropdown';

const ASSIGNEE_OPTION = { key: 'assignee', text: 'Assignee', value: 'assignee' };
const SECTIONS_TO_IGNORE_OPTIONS = [
  { key: 'taskMetadata', text: 'Status & Priority', value: 'taskMetadata' },
  ASSIGNEE_OPTION,
  { key: 'formAttribute', text: 'Form', value: 'formAttribute' },
  // { key: 'taskTypeAttribute', text: 'TaskType Attributes', value: 'taskTypeAttribute' },
];

const generateMultipleObject = {
  tasksToCopy: {},
  taskCount: 0,
};

function pageToView({ pageCount, childTasks, handleChange, taskGenerateObject, parentTask }) {
  if (pageCount === 0) {
    return (
      <ChildTaskSelector
        childTasks={childTasks}
        handleChange={handleChange}
        taskGenerateObject={taskGenerateObject}
        parentTask={parentTask}
      />
    );
  }
  if (pageCount === 1) {
    return <TaskNumberSelector handleChange={handleChange} taskGenerateObject={taskGenerateObject} />;
  }
  if (pageCount === 2) {
    return <ConfirmContent taskGenerateObject={taskGenerateObject} childTasks={childTasks} />;
  }
  return null;
}

function computeHardLinkTasks({ hardLinkedTasks, reqTaskToCopy, tasksToCopyClone }) {
  hardLinkedTasks.forEach((tId) => {
    if (reqTaskToCopy) {
      tasksToCopyClone[tId] = { ...reqTaskToCopy };
    } else {
      delete tasksToCopyClone[tId];
    }
  });
}

function createSameAssigneeAndHardLinkTaskMap(childTasks) {
  const hardLinkTasksDict = {};
  childTasks.forEach((childTask) => {
    const { immediateNextTo } = childTask;
    if (immediateNextTo) {
      if (!hardLinkTasksDict[immediateNextTo]) {
        hardLinkTasksDict[immediateNextTo] = new Set();
      }
      hardLinkTasksDict[immediateNextTo].add(childTask.id);
    }
  });
  return { hardLinkTasksDict };
}

function ChildTaskSelector({ childTasks, taskGenerateObject, handleChange, parentTask }) {
  const [workflowStepsDict, hardLinkTasksMap] = useMemo(() => {
    const workflow = parentTask.taskType.workflowVersions.find((wf) => wf.version === 'v1');
    const workflowSteps = workflow?.workflowSteps ?? [];
    const workflowStepsKeyed = _.keyBy(workflowSteps, 'workflowStepId');
    const { hardLinkTasksDict } = createSameAssigneeAndHardLinkTaskMap(childTasks);
    return [workflowStepsKeyed, hardLinkTasksDict];
  }, [parentTask]);

  const tasksToCopy = _.get(taskGenerateObject, 'tasksToCopy');

  const onChange = (event, taskId, data, isDropdown) => {
    let value = '';
    let name = '';
    if (isDropdown) {
      value = data?.value;
      name = data?.name;
    } else {
      value = event.target.checked;
      name = event.target.name;
    }
    const tasksToCopyClone = JSON.parse(JSON.stringify(tasksToCopy));
    let hardLinkedTasks = [];
    if (hardLinkTasksMap[taskId]) {
      hardLinkedTasks = [...hardLinkTasksMap[taskId]];
    }
    const reqTaskToCopy = tasksToCopyClone[taskId];
    if (name === 'sectionsToIgnore') {
      if (reqTaskToCopy?.sectionsToIgnore) {
        reqTaskToCopy.sectionsToIgnore = value;
      }
    } else if (value) {
      tasksToCopyClone[taskId] = { sectionsToIgnore: [] };
    } else {
      delete tasksToCopyClone[taskId];
    }
    if (hardLinkedTasks?.length) {
      computeHardLinkTasks({ hardLinkedTasks, reqTaskToCopy: tasksToCopyClone[taskId], tasksToCopyClone });
    }
    handleChange(null, true, tasksToCopyClone, 'tasksToCopy');
  };

  if (!_.size(childTasks)) {
    return <div>No task to copy</div>;
  }

  return (
    <div>
      <div style={{ margin: '10px 0' }}>Specify which task you would like to copy</div>
      <Grid columns={2} divided style={{ marginTop: '10px' }}>
        <Grid.Row>
          <Grid.Column>Tasks to Copy</Grid.Column>
          <Grid.Column>Sections To Ignore from copying </Grid.Column>
        </Grid.Row>
        {childTasks.map((step) => (
          <Grid.Row>
            <Grid.Column>
              <Checkbox
                label={_.get(step, 'taskType.name')}
                name={step.id}
                id={step.id}
                checked={tasksToCopy[step.id] || false}
                onChange={(e) => {
                  onChange(e, step.id);
                }}
                disabled={!!workflowStepsDict[step.workflowStepId]?.immediateNextTo}
              />
            </Grid.Column>
            <Grid.Column>
              <CustomDropdown
                placeholder="Sections to ignore"
                search
                selection
                multiple
                options={SECTIONS_TO_IGNORE_OPTIONS}
                value={tasksToCopy[step.id]?.sectionsToIgnore ?? []}
                onChange={(e, data) => {
                  onChange(e, step.id, data, true);
                }}
                disabled={!tasksToCopy[step.id] || !!workflowStepsDict[step.workflowStepId]?.immediateNextTo}
                name="sectionsToIgnore"
              />{' '}
            </Grid.Column>
          </Grid.Row>
        ))}
      </Grid>
    </div>
  );
}

ChildTaskSelector.propTypes = {
  handleChange: PropTypes.func.isRequired,
  taskGenerateObject: PropTypes.object,
  childTasks: PropTypes.array,
  parentTask: PropTypes.object,
};

function TaskNumberSelector({ handleChange, taskGenerateObject }) {
  const count = _.get(taskGenerateObject, 'taskCount');
  return (
    <div>
      <div>Specify the number of tasks you would like to generate</div>
      <div style={{ marginTop: '10px' }}>
        <label>
          Count
          <span className="mandatory-field">*</span>
        </label>
        <div>
          <Input
            type="text"
            value={count}
            onChange={(e) => {
              handleChange(e, false);
            }}
            name="taskCount"
          />
        </div>
      </div>
    </div>
  );
}

TaskNumberSelector.propTypes = {
  handleChange: PropTypes.func.isRequired,
  taskGenerateObject: PropTypes.object,
};

function ConfirmContent({ taskGenerateObject, childTasks }) {
  const count = taskGenerateObject.taskCount;
  const childTaskLength = (childTasks || []).length;
  return (
    <div>
      <div>
        {count} Parent Task(s) Generated with {childTaskLength} Subtasks each
      </div>
      <div>Please note that any unsaved data will not be copied .</div>
    </div>
  );
}

ConfirmContent.propTypes = {
  childTasks: PropTypes.array,
  taskGenerateObject: PropTypes.object,
};

function GenerateMultipleModal({ open, handleModal, task }) {
  const dispatch = useDispatch();
  const [pageCount, handlePage] = useState(0);
  const [taskGenerateObject, handleTaskGeneration] = useState(generateMultipleObject);
  const [error, handleError] = useState('');
  const [childTasks, handleChildTasks] = useState([]);
  const [loading, handleLoading] = useState(false);

  const fetchChildTasks = useCallback(async (parentTask) => {
    try {
      handleLoading(true);
      const { childTasks: steps } = await ApiService.getChildTasksByParentTaskId(parentTask?.id);
      const workflow = parentTask.taskType.workflowVersions.find((wf) => wf.version === 'v1');
      const workflowSteps = workflow?.workflowSteps ?? [];
      const stepsDictByWorkflowStepId = _.keyBy(steps, 'workflowStepId');
      const childTaskList = [];
      workflowSteps.forEach((step) => {
        const reqTask = stepsDictByWorkflowStepId[step.workflowStepId];
        if (reqTask) {
          childTaskList.push(reqTask);
        }
      });
      handleChildTasks(childTaskList);
      handleLoading(false);
    } catch (err) {
      handleLoading(false);
      handleError('Fetching Child Tasks Failed');
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchChildTasks(task);
  }, [fetchChildTasks]);

  const handleNext = () => {
    handleError('');
    handlePage((prevPageCount) => prevPageCount + 1);
  };
  const handlePrevious = () => {
    handleError('');
    handlePage((prevPageCount) => prevPageCount - 1);
  };

  const handleChange = (event, isCalledFromCheckbox, data, fieldName) => {
    const reqObj = { ...taskGenerateObject };
    const value = !isCalledFromCheckbox ? event.target.value : data;
    let field = null;
    if (!isCalledFromCheckbox) {
      field = event.target.name;
    } else {
      field = fieldName;
    }
    reqObj[field] = value;
    handleTaskGeneration(reqObj);
  };

  const handleGenerate = async () => {
    try {
      handleModal(false);
      await dispatch(generateMultipleTask({ ...taskGenerateObject, taskId: task.id }));
    } catch (e) {
      handleModal(false);
      console.log(e);
    }
  };

  const { taskCount } = taskGenerateObject;

  return (
    <Modal open={open} size="tiny">
      <Modal.Header>Generate Multiple Tasks</Modal.Header>
      {loading ? (
        <Segment>
          <Dimmer active inverted>
            <Loader active inline="centered" />
          </Dimmer>
        </Segment>
      ) : (
        <Modal.Content>
          {pageToView({ pageCount, childTasks, handleChange, taskGenerateObject, parentTask: task })}
        </Modal.Content>
      )}
      {error && <div className="errorText">*{error}</div>}
      <Modal.Actions>
        <div style={{ padding: '10px', float: 'right', display: 'flex' }}>
          <Joyn2Button
            size="large"
            type="button"
            label="Cancel"
            btnType="secondary"
            onClick={() => handleModal(false)}
            margin="0 10px 0 0"
          />
          {_.size(childTasks) ? (
            <>
              {pageCount > 0 ? (
                <Joyn2Button
                  size="large"
                  label="Previous"
                  type="submit"
                  icon="angle left"
                  onClick={() => handlePrevious()}
                  margin="0 10px 0 0"
                />
              ) : null}
              {pageCount < 2 ? (
                <Joyn2Button
                  size="large"
                  label="Next"
                  type="submit"
                  icon="angle right"
                  onClick={
                    // eslint-disable-next-line no-nested-ternary
                    pageCount === 0 && error
                      ? null
                      : taskCount === 0 && pageCount === 1
                      ? () => handleError('Task count sholud be more than 0')
                      : () => handleNext()
                  }
                />
              ) : (
                <Joyn2Button
                  size="large"
                  type="submit"
                  label="Generate"
                  onClick={() => handleGenerate(taskGenerateObject)}
                />
              )}
            </>
          ) : (
            <></>
          )}
        </div>
      </Modal.Actions>
    </Modal>
  );
}

GenerateMultipleModal.propTypes = {
  handleModal: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  task: PropTypes.object,
};
export default GenerateMultipleModal;
